import { createApi } from "@reduxjs/toolkit/query/react";
import { components, operations } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

// Data types from the API schema
type CreativeMetricsResponse = components["schemas"]["CreativeMetricsResponse"];
type MergedCreativeMetricsResponse =
  | components["schemas"]["CreativeMetricsResponse"]
  | null;

// Response type
type ApiResponseCreativeMetricsResponse =
  components["schemas"]["ApiResponse_CreativeMetricsResponse_"];

// Parameter types from operations
type GetCreativeMetricsParams = operations["GetCreativeMetrics"]["parameters"];
type GetMergedCreativeMetricsParams =
  operations["GetMergedCampaignCreativeMetrics"]["parameters"];

export const creativeMetricsApi = createApi({
  reducerPath: "creativeMetricsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["CreativeMetrics"],
  endpoints: (builder) => ({
    // Get creative metrics for a specific internal campaign
    getCreativeMetrics: builder.query<
      CreativeMetricsResponse,
      GetCreativeMetricsParams
    >({
      query: (params) => {
        // Extract path parameters
        const internalCampaignId = params.path.internalCampaignId;
        let queryString = `/creative-metrics/${internalCampaignId}`;

        // Handle optional query parameters
        if (params.query) {
          const urlParams = new URLSearchParams();

          if (params.query.startDate) {
            urlParams.append("startDate", params.query.startDate);
          }

          if (params.query.endDate) {
            urlParams.append("endDate", params.query.endDate);
          }

          const queryParams = urlParams.toString();
          if (queryParams) queryString += `?${queryParams}`;
        }

        return queryString;
      },
      transformResponse: (response: ApiResponseCreativeMetricsResponse) =>
        response.data as CreativeMetricsResponse,
      providesTags: ["CreativeMetrics"],
    }),
    getMergedCreativeMetrics: builder.query<
      MergedCreativeMetricsResponse,
      GetMergedCreativeMetricsParams
    >({
      query: (params) => {
        // Extract path parameters
        const mergedCampaignId = params.path.mergedCampaignId;
        let queryString = `/creative-metrics/merged/${mergedCampaignId}`;

        // Handle optional query parameters
        if (params.query) {
          const urlParams = new URLSearchParams();

          if (params.query.startDate) {
            urlParams.append("startDate", params.query.startDate);
          }

          if (params.query.endDate) {
            urlParams.append("endDate", params.query.endDate);
          }

          const queryParams = urlParams.toString();
          if (queryParams) queryString += `?${queryParams}`;
        }

        return queryString;
      },
      transformResponse: (response: ApiResponseCreativeMetricsResponse) =>
        response.data,
      providesTags: ["CreativeMetrics"],
    }),
  }),
});

export const { useGetCreativeMetricsQuery, useGetMergedCreativeMetricsQuery } =
  creativeMetricsApi;
